import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { IHistoryView } from '../../../shared/history/history.model';
import { HistoryViewComponent } from '../../../shared/history/history-view/history-view.component';
import { RabatKampagneService } from 'src/app/api/services';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { RabatKampagneDto } from '../../../../api/models/OK/Egis/Service/Models/DTO/rabat-kampagne-dto';
import { RabatkampagnerEditDialogComponent } from '../rabatkampagner-edit-dialog/rabatkampagner-edit-dialog.component';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { RabatTypeEnum } from '../../../../api/models/OK/Egis/Service/Models/ValueObjects/rabat-type-enum';

@Component({
  selector: 'app-rabatkampagner-list',
  templateUrl: './rabatkampagner-list.component.html',
  styleUrls: ['./rabatkampagner-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RabatkampagnerListComponent implements OnInit {
  rabatkampagner: RabatKampagneDto[] = [];
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(HistoryViewComponent) historyViewComponent: HistoryViewComponent;
  displayedColumns: string[] = ['name', 'rabat', 'rabatType', 'partnerShare', 'actions'];
  historyView: IHistoryView;

  constructor(
    private dialog: MatDialog,
    private service: RabatKampagneService,
    private auth: AuthService,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private notifications: NotificationService,
  ) {}

  get canEdit() {
    return this.auth.isAdministrator;
  }

  get rabatkampagnerSortedByName() {
    return this.rabatkampagner.sort((a, b) => (a.name < b.name ? -1 : 1));
  }

  async ngOnInit(): Promise<void> {
    await this.fetchRabatkampagnerAsync();
  }

  async edit(row: RabatKampagneDto) {
    this.openDialog(row);
  }

  showhistory(row: RabatKampagneDto) {
    this.historyView = { entityId: row.id, historyType: AuditHistoryEnum.Rabatkampagne, title: row.name };
  }

  add() {
    this.openDialog({} as RabatKampagneDto);
  }

  openDialog(rabatkampagne: RabatKampagneDto) {
    const dialogRef = this.dialog.open(RabatkampagnerEditDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: rabatkampagne,
    });

    dialogRef.afterClosed().subscribe(async (changedRabatkampagne) => {
      const reload = this.historyView && rabatkampagne && rabatkampagne.name !== rabatkampagne.name;
      if (changedRabatkampagne) {
        const existingRabatkampagne = this.rabatkampagner.find((p) => p.id === changedRabatkampagne.id);

        if (existingRabatkampagne) {
          this.notifications.success(`Rabatkampagne ${changedRabatkampagne.name} er opdateret`);
          Object.assign(existingRabatkampagne, changedRabatkampagne);
        } else {
          this.notifications.success(`Rabatkampagne ${changedRabatkampagne.name} er oprettet`);
          this.rabatkampagner.push(changedRabatkampagne);
        }

        this.table.renderRows();
        if (rabatkampagne) {
          if (reload) {
            this.showhistory(changedRabatkampagne);
          }
          this.historyViewComponent.render();
        } else {
          this.historyView = null;
        }
      }
    });
  }

  getRabatText(rabatkampagne: RabatKampagneDto) {
    switch (rabatkampagne.rabatType) {
      case RabatTypeEnum.NoRabat:
        return '';
      case RabatTypeEnum.KontantEngangsRabat:
      case RabatTypeEnum.RecurringKontantRabat:
        return rabatkampagne.kontant + ' kr';
      case RabatTypeEnum.ProcentEngangsRabat:
      case RabatTypeEnum.RecurringProcentRabat:
        return rabatkampagne.procent + '%';
    }
    return '';
  }

  getPartnerShareText(rabatkampagne: RabatKampagneDto) {
    switch (rabatkampagne.partnerShare) {
      case null:
        return 'Betalt ifølge servicepartnerandel';
      case 100:
        return 'Betalt af servicepartner';
      case 0:
        return 'Betalt af OK';
      default:
        return rabatkampagne.partnerShare + '%';
    }
  }

  async fetchRabatkampagnerAsync() {
    this.loader.show();
    await this.service
      .getRabatKampagner({
        caller: EGIS_SERVICE_CALLER,
      })
      .toPromise()
      .then((respone) => {
        this.rabatkampagner = respone;
      })
      .catch((error) => {
        this.errorHandler.HandleError(error);
      })
      .finally(() => {
        this.loader.hide();
      });
  }
}
